<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { useI18n } from "vue-i18n"

import { useRedirect } from "@core/composables"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Ajout terminé",
            messageLine1: "Les bâtiments ont été créés.",
            messageLine2Before:
                "Les consentements sont disponibles sur la page ",
            messageLink: "Consentements",
            messageLine2After: ".",
            close: "Fermer",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
interface Props {
    /**
     * Désactive le bouton de fermeture
     */
    noSubmit?: boolean
}

interface Emits {
    /**
     * Émis au clic sur « Fermer » — le parent ferme la dialog (ex. SourceMultiAppendDialog) ou le parcours.
     */
    (e: "click:quit"): void
}

// ================================
// PROPS & EMITS
// ================================
withDefaults(defineProps<Props>(), {
    noSubmit: false,
})

const emit = defineEmits<Emits>()

const { redirectWrapper } = useRedirect()

// ================================
// INTERACTIONS
// ================================
/**
 * Émet la fermeture du parcours / de la dialog parente
 */
function onClickQuit() {
    emit("click:quit")
}

/**
 * Ouvre la page liste des consentements (`/consentements`).
 */
function onNavigateToConsentements(): void {
    redirectWrapper("/consentements")
}
</script>

<template>
    <div
        class="source-form-multi-finish flex flex-col items-center gap-6 w-full max-w-[640px]"
    >
        <!-- ================================ -->
        <!-- HEADER -->
        <!-- ================================ -->
        <header class="flex flex-col items-center gap-6 mb-6">
            <h1
                class="text-lg font-semibold text-blue-700 flex items-center gap-4"
            >
                {{ t("title") }}
                <i
                    class="pi pi-check text-green-600"
                    style="font-size: 1.25rem"
                ></i>
            </h1>
        </header>

        <!-- ================================ -->
        <!-- MESSAGE (post-signature uniquement) -->
        <!-- ================================ -->
        <p class="text-sm text-center text-gray-700">
            {{ t("messageLine1") }}
        </p>
        <p class="text-sm text-center text-gray-700">
            {{ t("messageLine2Before") }}
            <slot name="consent-link">
                <button
                    type="button"
                    class="font-medium text-blue-600 hover:text-blue-800 underline p-0 border-0 bg-transparent cursor-pointer inline"
                    @click="onNavigateToConsentements"
                >
                    {{ t("messageLink") }}
                </button>
            </slot>
            {{ t("messageLine2After") }}
        </p>

        <slot></slot>

        <!-- ================================ -->
        <!-- FOOTER (même style que « Terminer » dans SourceMultiAppendFormInvitationSent) -->
        <!-- ================================ -->
        <div class="w-full flex justify-center">
            <BaseButton
                :label="t('close')"
                severity="secondary"
                class="max-w-[15rem] w-full"
                :disabled="noSubmit"
                @click="onClickQuit"
            />
        </div>
    </div>
</template>
